import { SxlEvaluator } from '@saviynt/savxl';

import { type EntityInitialData } from '../../FormPreview/data-store/initialDataSlice';

interface DateEvaluationContext {
  values: Record<string, unknown>;
  entityData: EntityInitialData | null;
}

// Single date evaluator instance for performance
const dateEvaluator = new SxlEvaluator();

/**
 * Evaluate date expression and return Date object using SAVXL
 */
export function evaluateDateExpression(
  expression: string,
  context: DateEvaluationContext
): Date | null {
  if (!expression.trim()) {
    return null;
  }

  try {
    // Clear previous variables for clean state
    dateEvaluator.clearVariables();

    // Set entity data variables first (lower priority)
    if (context.entityData) {
      Object.entries(context.entityData).forEach(([key, value]) => {
        if (value) {
          dateEvaluator.setVariable(key, value);
        }
      });
    }

    // Set context values as variables (higher priority - overwrites entityData)
    if (context.values) {
      Object.entries(context.values).forEach(([key, value]) => {
        // Convert string dates to Date objects
        if (typeof value === 'string') {
          const parsedDate = new Date(value);

          if (!Number.isNaN(parsedDate.getTime())) {
            dateEvaluator.setVariable(key, parsedDate);
          } else {
            dateEvaluator.setVariable(key, value);
          }
        } else if (value instanceof Date) {
          const formatted = value.toISOString().split('T')[0];

          dateEvaluator.setVariable(key, new Date(formatted));
        } else {
          dateEvaluator.setVariable(key, value);
        }
      });
    }

    const result = dateEvaluator.evaluate(expression);

    if (result instanceof Date && !Number.isNaN(result.getTime())) {
      return result;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Evaluate date validation expression and return boolean using SAVXL
 */
export function evaluateDateValidation(
  expression: string,
  context: DateEvaluationContext
): boolean {
  if (!expression.trim()) {
    return true;
  }

  try {
    // Clear previous variables for clean state
    dateEvaluator.clearVariables();

    // Set entity data variables first (lower priority)
    if (context.entityData) {
      Object.entries(context.entityData).forEach(([key, value]) => {
        if (value) {
          dateEvaluator.setVariable(key, value);
        }
      });
    }

    // Set context values as variables (higher priority - overwrites entityData)
    if (context.values) {
      Object.entries(context.values).forEach(([key, value]) => {
        // Convert string dates to Date objects
        if (typeof value === 'string') {
          const parsedDate = new Date(value);

          if (!Number.isNaN(parsedDate.getTime())) {
            dateEvaluator.setVariable(key, parsedDate);
          } else {
            dateEvaluator.setVariable(key, value);
          }
        } else if (value instanceof Date) {
          const formatted = value.toISOString().split('T')[0];

          dateEvaluator.setVariable(key, new Date(formatted));
        } else {
          dateEvaluator.setVariable(key, value);
        }
      });
    }

    // Use type-safe boolean evaluation
    return dateEvaluator.evaluateAs(expression, 'boolean');
  } catch {
    return true;
  }
}

export default evaluateDateExpression;
